// src/types.ts
var _ConnectError = class _ConnectError extends Error {
  constructor(message, status, statusText, config, response, cause) {
    super(message);
    this.status = status;
    this.statusText = statusText;
    this.config = _ConnectError._redactConfig(config);
    this.response = response;
    this.cause = cause;
    this.name = "ConnectError";
  }
  /** Whether this was a network error (no response) */
  get isNetworkError() {
    return this.status === void 0;
  }
  /** Whether this was a timeout */
  get isTimeout() {
    return this.cause?.name === "AbortError" || this.message.includes("timeout");
  }
  /** Whether this is a server error (5xx) */
  get isServerError() {
    return this.status !== void 0 && this.status >= 500;
  }
  /** Whether retrying might help */
  get isRetryable() {
    return this.isNetworkError || this.isTimeout || this.isServerError;
  }
  static _redactConfig(config) {
    if (!config.headers) return config;
    const redacted = { ...config, headers: { ...config.headers } };
    for (const key of _ConnectError.REDACTED_HEADERS) {
      if (redacted.headers[key]) redacted.headers[key] = "[REDACTED]";
      const lower = key.toLowerCase();
      for (const hk of Object.keys(redacted.headers)) {
        if (hk.toLowerCase() === lower && redacted.headers[hk] !== "[REDACTED]") {
          redacted.headers[hk] = "[REDACTED]";
        }
      }
    }
    return redacted;
  }
};
/** Headers that are redacted from error.config to prevent token leaks in logs */
_ConnectError.REDACTED_HEADERS = ["authorization", "x-api-key", "cookie", "x-auth-token"];
var ConnectError = _ConnectError;

// src/retry.ts
var IDEMPOTENT_METHODS = /* @__PURE__ */ new Set(["GET", "HEAD", "OPTIONS", "PUT", "DELETE"]);
var RETRYABLE_STATUS_CODES = /* @__PURE__ */ new Set([408, 429, 500, 502, 503, 504]);
function resolveRetryConfig(config) {
  return {
    maxAttempts: config?.maxAttempts ?? 3,
    baseDelay: config?.baseDelay ?? 1e3,
    maxDelay: config?.maxDelay ?? 3e4,
    multiplier: config?.multiplier ?? 2,
    jitter: config?.jitter ?? true,
    retryMethods: new Set(config?.retryMethods ?? IDEMPOTENT_METHODS),
    retryStatusCodes: new Set(config?.retryStatusCodes ?? RETRYABLE_STATUS_CODES),
    retryOn: config?.retryOn,
    onRetry: config?.onRetry
  };
}
function calculateDelay(attempt, config) {
  const exponential = config.baseDelay * Math.pow(config.multiplier, attempt);
  const capped = Math.min(exponential, config.maxDelay);
  if (!config.jitter) return capped;
  return Math.floor(Math.random() * capped);
}
async function shouldRetry(error, attempt, config) {
  if (attempt >= config.maxAttempts) return false;
  if (config.retryOn) {
    return config.retryOn(error, attempt);
  }
  if (error.config.method && !config.retryMethods.has(error.config.method)) {
    return false;
  }
  if (error.isNetworkError || error.isTimeout) return true;
  if (error.status && config.retryStatusCodes.has(error.status)) return true;
  return false;
}
function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    }, { once: true });
  });
}
async function withRetry(fn, config = {}, isRetryable, signal) {
  const resolved = resolveRetryConfig(config);
  let lastError;
  for (let attempt = 0; attempt <= resolved.maxAttempts; attempt++) {
    try {
      return await fn(attempt);
    } catch (err) {
      lastError = err;
      if (attempt >= resolved.maxAttempts) break;
      const retry = isRetryable ? await isRetryable(err, attempt) : true;
      if (!retry) break;
      const delay = calculateDelay(attempt, resolved);
      resolved.onRetry?.(err, attempt, delay);
      await sleep(delay, signal);
    }
  }
  throw lastError;
}

// src/client.ts
var HttpClient = class {
  constructor(config = {}) {
    this._onRequest = [];
    this._onResponse = [];
    this._onError = [];
    this._config = { timeout: 3e4, ...config };
    if (config.onRequest) this._onRequest = [...config.onRequest];
    if (config.onResponse) this._onResponse = [...config.onResponse];
    if (config.onError) this._onError = [...config.onError];
  }
  // ─── Interceptor management ──────────────────────
  addRequestInterceptor(fn) {
    this._onRequest.push(fn);
    return () => {
      const i = this._onRequest.indexOf(fn);
      if (i !== -1) this._onRequest.splice(i, 1);
    };
  }
  addResponseInterceptor(fn) {
    this._onResponse.push(fn);
    return () => {
      const i = this._onResponse.indexOf(fn);
      if (i !== -1) this._onResponse.splice(i, 1);
    };
  }
  addErrorInterceptor(fn) {
    this._onError.push(fn);
    return () => {
      const i = this._onError.indexOf(fn);
      if (i !== -1) this._onError.splice(i, 1);
    };
  }
  // ─── HTTP methods ────────────────────────────────
  get(url, config) {
    return this.request({ ...config, url, method: "GET" });
  }
  post(url, body, config) {
    return this.request({ ...config, url, method: "POST", body });
  }
  put(url, body, config) {
    return this.request({ ...config, url, method: "PUT", body });
  }
  patch(url, body, config) {
    return this.request({ ...config, url, method: "PATCH", body });
  }
  delete(url, config) {
    return this.request({ ...config, url, method: "DELETE" });
  }
  // ─── Core request ────────────────────────────────
  async request(config) {
    let reqConfig = {
      method: "GET",
      timeout: this._config.timeout,
      responseType: "json",
      ...config,
      headers: { ...this._config.headers, ...config.headers }
    };
    for (const interceptor of this._onRequest) {
      reqConfig = await interceptor(reqConfig);
    }
    const fullUrl = this._buildUrl(reqConfig);
    if (this._config.retry !== false && !reqConfig.skipRetry) {
      const retryConfig = resolveRetryConfig(
        typeof this._config.retry === "object" ? this._config.retry : void 0
      );
      return this._executeWithRetry(reqConfig, fullUrl, retryConfig);
    }
    return this._execute(reqConfig, fullUrl);
  }
  // ─── Internal ────────────────────────────────────
  async _executeWithRetry(config, fullUrl, retryConfig) {
    let lastError;
    for (let attempt = 0; attempt <= retryConfig.maxAttempts; attempt++) {
      try {
        return await this._execute(config, fullUrl);
      } catch (err) {
        if (!(err instanceof ConnectError)) throw err;
        lastError = err;
        if (attempt >= retryConfig.maxAttempts) break;
        const retry = await shouldRetry(err, attempt, retryConfig);
        if (!retry) break;
        let delay = calculateDelay(attempt, retryConfig);
        const retryAfter = err.response?.headers?.get("retry-after");
        if (retryAfter) {
          const parsed = Number(retryAfter);
          const serverDelay = isNaN(parsed) ? Math.max(0, new Date(retryAfter).getTime() - Date.now()) : parsed * 1e3;
          if (serverDelay > 0) delay = Math.max(delay, serverDelay);
          if (delay > 6e4) break;
        }
        retryConfig.onRetry?.(err, attempt, delay);
        await sleep(delay, config.signal);
      }
    }
    throw lastError;
  }
  async _execute(config, fullUrl) {
    const fetchFn = this._config.fetch ?? globalThis.fetch;
    const start = Date.now();
    const init = {
      method: config.method,
      headers: config.headers,
      signal: config.signal,
      credentials: config.credentials ?? this._config.credentials
    };
    if (config.body !== void 0 && config.body !== null) {
      if (typeof config.body === "string" || config.body instanceof FormData || config.body instanceof Blob || config.body instanceof ArrayBuffer) {
        init.body = config.body;
      } else {
        init.body = JSON.stringify(config.body);
        if (!config.headers?.["content-type"] && !config.headers?.["Content-Type"]) {
          init.headers = { ...init.headers, "content-type": "application/json" };
        }
      }
    }
    let timeoutId;
    let abortController;
    if (config.timeout && !config.signal) {
      abortController = new AbortController();
      init.signal = abortController.signal;
      timeoutId = setTimeout(() => abortController.abort(), config.timeout);
    }
    let response;
    try {
      response = await fetchFn(fullUrl, init);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      const connectError = new ConnectError(
        error.name === "AbortError" ? `Request timeout after ${config.timeout}ms: ${config.method} ${fullUrl}` : `Network error: ${config.method} ${fullUrl}: ${error.message}`,
        void 0,
        void 0,
        config,
        void 0,
        error
      );
      throw await this._runErrorInterceptors(connectError);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
    const duration = Date.now() - start;
    let data;
    try {
      if (response.status === 204) {
        data = void 0;
      } else if (config.responseType === "text") {
        data = await response.text();
      } else if (config.responseType === "blob") {
        data = await response.blob();
      } else if (config.responseType === "arraybuffer") {
        data = await response.arrayBuffer();
      } else {
        const text = await response.text();
        try {
          data = text ? JSON.parse(text) : void 0;
        } catch {
          data = text;
        }
      }
    } catch {
      data = void 0;
    }
    let result = {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      config,
      duration
    };
    if (!response.ok) {
      const connectError = new ConnectError(
        `HTTP ${response.status}: ${config.method} ${fullUrl}`,
        response.status,
        response.statusText,
        config,
        result
      );
      throw await this._runErrorInterceptors(connectError);
    }
    for (const interceptor of this._onResponse) {
      result = await interceptor(result);
    }
    return result;
  }
  async _runErrorInterceptors(error) {
    for (const interceptor of this._onError) {
      const result = await interceptor(error);
      if (result && "data" in result && "status" in result) {
        return result;
      }
      if (result instanceof ConnectError) {
        error = result;
      }
    }
    return error;
  }
  _buildUrl(config) {
    let url = config.url;
    if (this._config.baseUrl && !url.startsWith("http://") && !url.startsWith("https://")) {
      const base = this._config.baseUrl.replace(/\/$/, "");
      const path = url.startsWith("/") ? url : `/${url}`;
      url = `${base}${path}`;
    }
    if (config.params) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(config.params)) {
        if (value !== void 0) {
          params.append(key, String(value));
        }
      }
      const qs = params.toString();
      if (qs) {
        url += (url.includes("?") ? "&" : "?") + qs;
      }
    }
    return url;
  }
};
function createClient(config) {
  return new HttpClient(config);
}

// src/network.ts
var NetworkMonitor = class {
  constructor(config = {}) {
    this._status = "unknown";
    this._listeners = /* @__PURE__ */ new Set();
    this._pingTimer = null;
    this._onOnline = null;
    this._onOffline = null;
    this._started = false;
    this._config = config;
  }
  get status() {
    return this._status;
  }
  get isOnline() {
    return this._status === "online";
  }
  get isOffline() {
    return this._status === "offline";
  }
  /** Subscribe to status changes. Returns unsubscribe function. */
  subscribe(fn) {
    this._listeners.add(fn);
    if (this._status !== "unknown") {
      fn(this._status);
    }
    return () => this._listeners.delete(fn);
  }
  /** Start monitoring */
  start() {
    if (this._started) return;
    this._started = true;
    if (typeof navigator !== "undefined" && "onLine" in navigator) {
      this._setStatus(navigator.onLine ? "online" : "offline");
    }
    if (typeof window !== "undefined") {
      this._onOnline = () => this._setStatus("online");
      this._onOffline = () => this._setStatus("offline");
      window.addEventListener("online", this._onOnline);
      window.addEventListener("offline", this._onOffline);
    }
    if (this._config.pingUrl) {
      this._doPing();
      this._pingTimer = setInterval(() => this._doPing(), this._config.pingInterval ?? 3e4);
    }
  }
  /** Stop monitoring */
  stop() {
    if (!this._started) return;
    this._started = false;
    if (typeof window !== "undefined") {
      if (this._onOnline) window.removeEventListener("online", this._onOnline);
      if (this._onOffline) window.removeEventListener("offline", this._onOffline);
      this._onOnline = null;
      this._onOffline = null;
    }
    if (this._pingTimer) {
      clearInterval(this._pingTimer);
      this._pingTimer = null;
    }
  }
  /** Manually trigger a ping check */
  async ping() {
    return this._doPing();
  }
  /** Dispose all listeners and stop */
  dispose() {
    this.stop();
    this._listeners.clear();
  }
  _setStatus(status) {
    if (status === this._status) return;
    this._status = status;
    for (const fn of [...this._listeners]) {
      try {
        fn(status);
      } catch {
      }
    }
  }
  async _doPing() {
    if (!this._config.pingUrl) return this._status === "online";
    const fetchFn = this._config.fetch ?? globalThis.fetch;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this._config.pingTimeout ?? 5e3);
    try {
      await fetchFn(this._config.pingUrl, {
        method: "HEAD",
        signal: controller.signal,
        cache: "no-store"
      });
      this._setStatus("online");
      return true;
    } catch {
      this._setStatus("offline");
      return false;
    } finally {
      clearTimeout(timeout);
    }
  }
};
function createNetworkMonitor(config) {
  return new NetworkMonitor(config);
}

// src/queue.ts
var queueIdCounter = 0;
var RequestQueue = class {
  constructor(config) {
    this._queue = [];
    this._replaying = false;
    this._replayPending = false;
    this._networkUnsub = null;
    this._initialized = false;
    this._config = config;
  }
  get size() {
    return this._queue.length;
  }
  get isEmpty() {
    return this._queue.length === 0;
  }
  get items() {
    return this._queue;
  }
  get isReplaying() {
    return this._replaying;
  }
  /** Start watching for online events and load persisted queue */
  async start() {
    if (this._config.storage && !this._initialized) {
      this._initialized = true;
      try {
        const saved = await this._config.storage.load();
        this._queue = saved;
      } catch {
        this._queue = [];
      }
    }
    if (this._config.networkMonitor) {
      this._networkUnsub = this._config.networkMonitor.subscribe((status) => {
        if (status === "online" && this._queue.length > 0) {
          if (this._replaying) {
            this._replayPending = true;
          } else {
            this.replay().then(() => {
              if (this._replayPending && this._queue.length > 0) {
                this._replayPending = false;
                this.replay();
              }
            });
          }
        }
      });
    }
  }
  /** Stop watching and clean up */
  stop() {
    if (this._networkUnsub) {
      this._networkUnsub();
      this._networkUnsub = null;
    }
  }
  /** Add a request to the queue */
  async enqueue(config) {
    if (config.skipQueue) {
      throw new Error("Request has skipQueue flag \u2014 should not be queued");
    }
    const maxSize = this._config.maxSize ?? 100;
    if (this._queue.length >= maxSize) {
      this._queue.shift();
    }
    const sanitizedConfig = { ...config };
    if (sanitizedConfig.headers) {
      sanitizedConfig.headers = { ...sanitizedConfig.headers };
      for (const key of ["authorization", "Authorization", "x-api-key", "X-Api-Key", "cookie", "Cookie"]) {
        delete sanitizedConfig.headers[key];
      }
    }
    const item = {
      id: `q_${Date.now()}_${++queueIdCounter}`,
      config: sanitizedConfig,
      queuedAt: Date.now(),
      retries: 0
    };
    this._queue.push(item);
    this._config.onChange?.(this._queue);
    await this._persist();
    return item;
  }
  /** Remove a specific request from the queue */
  async remove(id) {
    const index = this._queue.findIndex((r) => r.id === id);
    if (index === -1) return false;
    this._queue.splice(index, 1);
    this._config.onChange?.(this._queue);
    await this._persist();
    return true;
  }
  /** Clear all queued requests */
  async clear() {
    this._queue = [];
    this._config.onChange?.(this._queue);
    if (this._config.storage) {
      await this._config.storage.clear();
    }
  }
  /** Replay all queued requests (called automatically on online, or manually) */
  async replay() {
    if (this._replaying || this._queue.length === 0) {
      return { succeeded: 0, failed: 0 };
    }
    this._replaying = true;
    let succeeded = 0;
    let failed = 0;
    const maxAge = this._config.maxAge ?? 24 * 60 * 60 * 1e3;
    const now = Date.now();
    const toReplay = [...this._queue];
    this._queue = [];
    for (const item of toReplay) {
      if (now - item.queuedAt > maxAge) {
        continue;
      }
      try {
        const response = await this._config.execute(item.config);
        this._config.onReplaySuccess?.(item, response);
        succeeded++;
      } catch (err) {
        item.retries++;
        item.lastError = err instanceof Error ? err.message : String(err);
        this._queue.push(item);
        this._config.onReplayError?.(item, err instanceof Error ? err : new Error(String(err)));
        failed++;
      }
    }
    this._config.onChange?.(this._queue);
    await this._persist();
    this._replaying = false;
    return { succeeded, failed };
  }
  /** Dispose */
  dispose() {
    this.stop();
    this._queue = [];
  }
  async _persist() {
    if (this._config.storage) {
      try {
        await this._config.storage.save(this._queue);
      } catch (err) {
        console.warn("[DryConnect queue] Failed to persist queue:", err);
      }
    }
  }
};
function createRequestQueue(config) {
  return new RequestQueue(config);
}

// src/sse.ts
var SSEClient = class {
  constructor(config) {
    this._listeners = /* @__PURE__ */ new Map();
    this._controller = null;
    this._reconnectAttempts = 0;
    this._reconnectTimer = null;
    this._connected = false;
    this._disposed = false;
    this._config = config;
  }
  get connected() {
    return this._connected;
  }
  /** Subscribe to a specific event type. Use '*' for all events. */
  on(eventType, listener) {
    if (!this._listeners.has(eventType)) {
      this._listeners.set(eventType, /* @__PURE__ */ new Set());
    }
    this._listeners.get(eventType).add(listener);
    return () => {
      this._listeners.get(eventType)?.delete(listener);
    };
  }
  /** Connect to the SSE endpoint */
  async connect() {
    if (this._disposed) return;
    this._controller?.abort();
    this._controller = new AbortController();
    const fetchFn = this._config.fetch ?? globalThis.fetch;
    const headers = {
      "accept": "text/event-stream",
      "cache-control": "no-cache",
      ...this._config.headers
    };
    if (this._lastEventId) {
      headers["last-event-id"] = this._lastEventId;
    }
    const init = {
      method: this._config.method ?? "GET",
      headers,
      signal: this._controller.signal
    };
    if (this._config.body && this._config.method === "POST") {
      init.body = typeof this._config.body === "string" ? this._config.body : JSON.stringify(this._config.body);
      if (!headers["content-type"]) {
        headers["content-type"] = "application/json";
      }
    }
    try {
      const response = await fetchFn(this._config.url, init);
      if (!response.ok) {
        throw new Error(`SSE connection failed: HTTP ${response.status}`);
      }
      const contentType = response.headers.get("content-type") ?? "";
      if (!contentType.includes("text/event-stream") && !contentType.includes("text/plain")) {
        throw new Error(`SSE: unexpected Content-Type "${contentType}", expected text/event-stream`);
      }
      if (!response.body) {
        throw new Error("SSE connection failed: no response body (streaming not supported)");
      }
      this._connected = true;
      this._reconnectAttempts = 0;
      this._config.onOpen?.();
      await this._readStream(response.body);
    } catch (err) {
      if (this._disposed) return;
      if (err.name === "AbortError") return;
      this._connected = false;
      this._config.onError?.(err instanceof Error ? err : new Error(String(err)));
      this._scheduleReconnect();
    }
  }
  /** Disconnect */
  disconnect() {
    this._cancelReconnect();
    this._controller?.abort();
    this._controller = null;
    this._connected = false;
    this._config.onClose?.();
  }
  /** Dispose all resources */
  dispose() {
    this._disposed = true;
    this.disconnect();
    this._listeners.clear();
  }
  async _readStream(body) {
    const decoder = new TextDecoder();
    const reader = body.getReader();
    let buffer = "";
    try {
      for (; ; ) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";
        for (const eventText of events) {
          if (!eventText.trim()) continue;
          const event = this._parseEvent(eventText);
          if (event) {
            this._emit(event);
          }
        }
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      throw err;
    } finally {
      reader.releaseLock();
    }
    this._connected = false;
    this._config.onClose?.();
    this._scheduleReconnect();
  }
  _parseEvent(text) {
    let id;
    let event = "message";
    let data = "";
    let retry;
    for (const line of text.split("\n")) {
      if (line.startsWith("id:")) {
        id = line.slice(3).trim();
      } else if (line.startsWith("event:")) {
        event = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        const content = line.slice(5);
        data += (data ? "\n" : "") + (content.startsWith(" ") ? content.slice(1) : content);
      } else if (line.startsWith("retry:")) {
        const val = parseInt(line.slice(6).trim(), 10);
        if (!isNaN(val)) retry = val;
      }
    }
    if (!data) return null;
    if (id) this._lastEventId = id;
    let parsedData;
    try {
      parsedData = JSON.parse(data);
    } catch {
      parsedData = data;
    }
    return { id, event, data: parsedData, retry };
  }
  _emit(event) {
    const listeners = this._listeners.get(event.event);
    if (listeners) {
      for (const fn of [...listeners]) {
        try {
          fn(event);
        } catch {
        }
      }
    }
    const wildcardListeners = this._listeners.get("*");
    if (wildcardListeners) {
      for (const fn of [...wildcardListeners]) {
        try {
          fn(event);
        } catch {
        }
      }
    }
  }
  _scheduleReconnect() {
    if (this._disposed) return;
    if (this._config.autoReconnect === false) return;
    const maxAttempts = this._config.maxReconnectAttempts ?? Infinity;
    if (this._reconnectAttempts >= maxAttempts) return;
    const delay = this._config.reconnectDelay ?? 3e3;
    this._reconnectAttempts++;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this.connect();
    }, delay);
  }
  _cancelReconnect() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }
};
function createSSEClient(config) {
  return new SSEClient(config);
}

// src/websocket.ts
function createWebSocket(config) {
  const {
    url: baseUrl,
    ticketProvider,
    ticketParam = "ticket",
    protocols,
    autoReconnect = true,
    reconnectDelay = 1e3,
    maxReconnectDelay = 3e4,
    maxReconnectAttempts = Infinity,
    jitter = true,
    heartbeatInterval = 3e4,
    heartbeatTimeout = 1e4,
    pingMessage = { type: "ping" },
    isPong = (d) => typeof d === "object" && d !== null && d.type === "pong" || d === "pong",
    parse = (d) => {
      try {
        return JSON.parse(d);
      } catch {
        return d;
      }
    },
    serialize = (d) => typeof d === "string" ? d : JSON.stringify(d),
    sendQueueSize = 50
  } = config;
  let ws = null;
  let status = "disconnected";
  let reconnectAttempts = 0;
  let reconnectTimer = null;
  let heartbeatTimer = null;
  let heartbeatTimeoutTimer = null;
  let intentionalClose = false;
  const sendQueue = [];
  let disposed = false;
  let authUnsub = null;
  let messagesSent = 0;
  let messagesReceived = 0;
  let lastHeartbeatRtt = null;
  let heartbeatSentAt = 0;
  const messageListeners = /* @__PURE__ */ new Set();
  const typedListeners = /* @__PURE__ */ new Map();
  function setStatus(s) {
    status = s;
    config.onStatus?.(s);
  }
  function stopHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    if (heartbeatTimeoutTimer) {
      clearTimeout(heartbeatTimeoutTimer);
      heartbeatTimeoutTimer = null;
    }
  }
  function startHeartbeat() {
    if (heartbeatInterval <= 0 || !ws) return;
    stopHeartbeat();
    heartbeatTimer = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        heartbeatSentAt = Date.now();
        ws.send(serialize(pingMessage));
        heartbeatTimeoutTimer = setTimeout(() => ws?.close(), heartbeatTimeout);
      }
    }, heartbeatInterval);
  }
  function scheduleReconnect() {
    if (!autoReconnect || reconnectAttempts >= maxReconnectAttempts || disposed) {
      setStatus("disconnected");
      return;
    }
    setStatus("reconnecting");
    const exponential = Math.min(reconnectDelay * Math.pow(2, reconnectAttempts), maxReconnectDelay);
    const delay = jitter ? Math.floor(Math.random() * exponential) : exponential;
    reconnectAttempts++;
    config.onReconnectAttempt?.(reconnectAttempts, delay);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      doConnect();
    }, delay);
  }
  async function doConnect() {
    if (disposed) return;
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      ws = null;
    }
    setStatus("connecting");
    intentionalClose = false;
    let connectUrl = baseUrl;
    const tokenProvider = ticketProvider ?? (config.auth ? async () => {
      const t = await config.auth.getValidToken();
      if (!t) throw new Error("No valid auth token");
      return t;
    } : null);
    if (tokenProvider) {
      try {
        const ticket = await tokenProvider();
        const sep = connectUrl.includes("?") ? "&" : "?";
        connectUrl = `${connectUrl}${sep}${ticketParam}=${encodeURIComponent(ticket)}`;
      } catch (err) {
        const authErr = err instanceof Error ? err : new Error(String(err));
        const shouldReconnect = config.onAuthFailure?.(authErr);
        if (shouldReconnect === false) {
          setStatus("disconnected");
          return;
        }
        scheduleReconnect();
        return;
      }
    }
    const socket = new WebSocket(connectUrl, protocols);
    ws = socket;
    socket.onopen = () => {
      if (ws !== socket) return;
      setStatus("connected");
      reconnectAttempts = 0;
      startHeartbeat();
      while (sendQueue.length > 0 && ws?.readyState === WebSocket.OPEN) {
        ws.send(serialize(sendQueue.shift()));
      }
    };
    socket.onmessage = (event) => {
      if (ws !== socket) return;
      const data = parse(typeof event.data === "string" ? event.data : String(event.data));
      if (isPong(data)) {
        if (heartbeatTimeoutTimer) {
          clearTimeout(heartbeatTimeoutTimer);
          heartbeatTimeoutTimer = null;
        }
        if (heartbeatSentAt > 0) {
          lastHeartbeatRtt = Date.now() - heartbeatSentAt;
          heartbeatSentAt = 0;
        }
        return;
      }
      messagesReceived++;
      config.onMessage?.(data);
      for (const fn of messageListeners) {
        try {
          fn(data);
        } catch {
        }
      }
      if (typeof data === "object" && data !== null && "type" in data) {
        const type = data.type;
        const listeners = typedListeners.get(type);
        if (listeners) {
          for (const fn of listeners) {
            try {
              fn(data);
            } catch {
            }
          }
        }
      }
    };
    socket.onclose = () => {
      if (ws !== socket) return;
      stopHeartbeat();
      if (!intentionalClose) scheduleReconnect();
      else setStatus("disconnected");
    };
    socket.onerror = (e) => {
      if (ws !== socket) return;
      config.onError?.(e);
    };
  }
  const client = {
    get status() {
      return status;
    },
    async connect() {
      await doConnect();
      if (config.auth && !authUnsub) {
        authUnsub = config.auth.subscribe((authStatus) => {
          if (authStatus === "expired" && status === "connected") {
            client.disconnect();
          } else if (authStatus === "authenticated" && status === "disconnected" && !intentionalClose) {
            reconnectAttempts = 0;
            doConnect();
          }
        });
      }
    },
    disconnect() {
      intentionalClose = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      stopHeartbeat();
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.close();
        ws = null;
      }
      setStatus("disconnected");
    },
    async reconnect() {
      client.disconnect();
      reconnectAttempts = 0;
      await doConnect();
    },
    send(data) {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(serialize(data));
        messagesSent++;
        return true;
      }
      if (sendQueueSize > 0) {
        if (sendQueue.length >= sendQueueSize) {
          const dropped = sendQueue.shift();
          config.onSendDrop?.(dropped);
        }
        sendQueue.push(data);
        return false;
      }
      return false;
    },
    get sendQueueLength() {
      return sendQueue.length;
    },
    /** Observability stats */
    get stats() {
      return { messagesSent, messagesReceived, lastHeartbeatRtt, sendQueueLength: sendQueue.length };
    },
    /** Subscribe to a topic (sends { type: 'subscribe', topic } to server) */
    subscribe(topic) {
      client.send({ type: "subscribe", topic });
    },
    /** Unsubscribe from a topic (sends { type: 'unsubscribe', topic } to server) */
    unsubscribe(topic) {
      client.send({ type: "unsubscribe", topic });
    },
    on(type, listener) {
      if (!typedListeners.has(type)) typedListeners.set(type, /* @__PURE__ */ new Set());
      typedListeners.get(type).add(listener);
      return () => typedListeners.get(type)?.delete(listener);
    },
    onMessage(listener) {
      messageListeners.add(listener);
      return () => messageListeners.delete(listener);
    },
    dispose() {
      disposed = true;
      client.disconnect();
      if (authUnsub) {
        authUnsub();
        authUnsub = null;
      }
      messageListeners.clear();
      typedListeners.clear();
    }
  };
  return client;
}

// src/dedup.ts
function createDeduplicator() {
  const inflight = /* @__PURE__ */ new Map();
  return {
    deduplicate(key, fn) {
      const existing = inflight.get(key);
      if (existing) return existing;
      const promise = fn().finally(() => {
        inflight.delete(key);
      });
      inflight.set(key, promise);
      return promise;
    },
    get inflight() {
      return inflight.size;
    },
    clear() {
      inflight.clear();
    }
  };
}
function requestKey(config) {
  const method = config.method ?? "GET";
  let key = `${method}:${config.url}`;
  if (config.body) {
    try {
      key += `:${JSON.stringify(config.body)}`;
    } catch {
    }
  }
  return key;
}

// src/paginate.ts
async function fetchAllPages(client, url, config = {}) {
  const {
    pageParam = "page",
    limitParam = "limit",
    limit = 100,
    startPage = 1,
    getItems = (data) => Array.isArray(data) ? data : data?.data ?? [],
    hasMore: hasMoreFn,
    params: extraParams = {},
    maxPages = 100,
    onProgress
  } = config;
  const allItems = [];
  let page = startPage;
  for (let i = 0; i < maxPages; i++) {
    const res = await client.get(url, {
      params: { ...extraParams, [pageParam]: page, [limitParam]: limit }
    });
    const items = getItems(res.data);
    allItems.push(...items);
    onProgress?.(allItems.length, page);
    const more = hasMoreFn ? hasMoreFn(res.data, items, page) : items.length >= limit;
    if (!more) break;
    page++;
  }
  return allItems;
}

// src/filter-builder.ts
function filter() {
  const filters = [];
  let sortField = null;
  let sortDir = "asc";
  let page = null;
  let limit = null;
  const rawParams = {};
  const builder = {
    where(field, opOrValue, value) {
      if (value !== void 0) {
        filters.push({ field, op: opOrValue, value });
      } else {
        filters.push({ field, value: opOrValue });
      }
      return builder;
    },
    sort(field, direction = "asc") {
      sortField = field;
      sortDir = direction;
      return builder;
    },
    paginate(p, l) {
      page = p;
      limit = l;
      return builder;
    },
    param(key, value) {
      rawParams[key] = value;
      return builder;
    },
    toParams() {
      const params = { ...rawParams };
      for (const f of filters) {
        if (f.value === null || f.value === void 0) continue;
        if (!f.op) {
          params[f.field] = String(f.value);
        } else if (f.op === "in" && Array.isArray(f.value)) {
          params[f.field] = f.value.join(",");
        } else {
          const opSuffix = { ">=": "_gte", "<=": "_lte", ">": "_gt", "<": "_lt", "!=": "_ne" }[f.op] ?? `_${f.op}`;
          params[`${f.field}${opSuffix}`] = String(f.value);
        }
      }
      if (sortField) {
        params["sort"] = sortDir === "desc" ? `-${sortField}` : sortField;
      }
      if (page !== null) params["page"] = page;
      if (limit !== null) params["limit"] = limit;
      return params;
    },
    toQueryString() {
      const params = this.toParams();
      return new URLSearchParams(
        Object.entries(params).map(([k, v]) => [k, String(v)])
      ).toString();
    }
  };
  return builder;
}

export { ConnectError, HttpClient, NetworkMonitor, RequestQueue, SSEClient, calculateDelay, createClient, createDeduplicator, createNetworkMonitor, createRequestQueue, createSSEClient, createWebSocket, fetchAllPages, filter, requestKey, shouldRetry, sleep, withRetry };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map